import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { CancelCrossIcon, Sheet, ArrowUpIcon, ArrowDownIcon } from "@activecollab/components";
import PropTypes from "prop-types";
import ErrorBoundary from "~/components/ErrorBoundry";
import { useObjectSheetContext } from "~/components/Sheet/ObjectSheetContext";
import { useDiscussionNavigation } from "~/repository";
import { useHistoryContext } from "~/routes/context";
import { getUpdateHideNotifications } from "~/store/selectors/selector.configOptions";
import { getDiscussionById } from "~/store/selectors/selector.discussions";
import Angie from "~/utils/angie";
import { Discussion } from "../Discussions/Discussion";
import { DiscussionTrashSlate } from "../Discussions/DiscussionTrashSlate";
import { useUnreadCount } from "../Hooks";
import { usePageTitle } from "../Hooks";

export const DiscussionSheet = React.forwardRef(({ id, projectId, open, onClose }, ref) => {
  const { t } = useTranslation();
  const { pathname, state } = useLocation();
  const navigate = useNavigate();
  const inListContext = new RegExp(`/projects/${projectId}/discussions$`).test(pathname);

  const discussion = useSelector((state) => getDiscussionById(state, id));
  const {
    data = {},
    isLoading,
    isFetched,
  } = useDiscussionNavigation(discussion?.project_id, id, {
    retry: 0,
    enabled: discussion?.project_id > 0,
  });

  const [edit, setEdit] = useState(false);

  const { closeSheet } = useObjectSheetContext();
  const { state: historyState } = useHistoryContext();
  const prevPageTitle = historyState[historyState.length - 1]?.title;
  const updatesHideNotifications = useSelector(getUpdateHideNotifications);
  const totalUnreadUpdates = useUnreadCount();

  usePageTitle(open ? discussion?.name : prevPageTitle, !updatesHideNotifications ? totalUnreadUpdates : 0);

  const handleClose = () => {
    setEdit(false);
    if (typeof onClose === "function") {
      onClose();
    }
  };

  const { next, prev } = data ?? {};

  const handlePrev = useCallback(() => {
    if (prev) {
      Angie.logger.info("Discussion navigation button clicked.", {
        direction: "prev",
        navigation_source: "sheet",
        navigation_type: "discussion",
      });
      navigate(`${pathname}?modal=Discussion-${prev}-${projectId}`);
    }
  }, [navigate, pathname, prev, projectId]);

  const handleNext = useCallback(() => {
    if (next) {
      Angie.logger.info("Discussion navigation button clicked.", {
        direction: "next",
        navigation_source: "sheet",
        navigation_type: "discussion",
      });
      navigate(`${pathname}?modal=Discussion-${next}-${projectId}`);
    }
  }, [navigate, next, pathname, projectId]);

  const showPrevNext = discussion && discussion.project_id === projectId && Boolean(prev || next);

  const loading = !isFetched || isLoading;

  const controls = useMemo(
    () =>
      showPrevNext && inListContext && !loading && !discussion?.is_trashed && !edit
        ? [
            {
              tooltip: t("Close"),
              onClick: closeSheet,
              icon: <CancelCrossIcon />,
              className: "task_modal_close_icon tw-order-1 sm:tw-order-1",
            },
            {
              tooltip: prev ? t("Previous Discussion") : t("You are at beginning of the list"),
              onClick: handlePrev,
              icon: <ArrowUpIcon />,
              disabled: !prev,
              className: "tw-order-3 sm:tw-order-2",
            },
            {
              tooltip: next ? t("Next Discussion") : t("You have reached the end of the list"),
              onClick: handleNext,
              icon: <ArrowDownIcon />,
              disabled: !next,
              className: "tw-order-2 sm:tw-order-3",
            },
          ]
        : [
            {
              tooltip: t("Close"),
              onClick: closeSheet,
              icon: <CancelCrossIcon />,
              className: "task_modal_close_icon",
            },
          ],
    [
      closeSheet,
      discussion?.is_trashed,
      edit,
      handleNext,
      handlePrev,
      inListContext,
      loading,
      next,
      prev,
      showPrevNext,
      t,
    ]
  );

  useEffect(() => {
    setEdit(state?.edit);
  }, [state]);

  const handleCloseForm = useCallback(() => {
    navigate(`${pathname}?modal=Discussion-${id}-${projectId}`, {
      replace: true,
    });
  }, [id, navigate, pathname, projectId]);

  return (
    <Sheet
      ref={ref}
      open={open}
      position="right"
      animation="right"
      mode="stretch"
      onClose={handleClose}
      controls={controls}
      className="task-modal-container"
    >
      <ErrorBoundary errorLocation="Discussion Sheet">
        <DiscussionTrashSlate
          id={id}
          projectId={projectId}
        >
          <Discussion
            id={id}
            projectId={projectId}
            edit={edit}
            setEdit={setEdit}
            onCloseForm={handleCloseForm}
            shouldOverflow
          />
        </DiscussionTrashSlate>
      </ErrorBoundary>
    </Sheet>
  );
});

DiscussionSheet.propTypes = {
  id: PropTypes.number,
  projectId: PropTypes.number,
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

DiscussionSheet.displayName = "DiscussionSheet";
